/**
 * The URL surface of the Brand Presence area — every route path, and the query vocabulary a link into
 * a screen carries — in one module that imports no screen.
 *
 * The routing container declares these paths and every navigation target — tab links, redirects and
 * the area default — is derived from the same entries, so a test can mount the real route table and
 * assert against the paths the router itself uses instead of restating them. A screen that reads a
 * filter out of the query string takes the parameter's name and its tokens from here too, so a link
 * built on one screen and read apart on another cannot spell one of them differently.
 */
import { ContentItemStatus, ContentRepresentation, IssueStatus } from '@edgebox/sync-core-data-definitions';

/** The path the host page hands over and the area is mounted at. */
export const BRAND_PRESENCE_BASE_PATH = '/brand-presence';

/**
 * The onboarding wizard's route paths, relative to {@link BRAND_PRESENCE_BASE_PATH}. Every URL here
 * keeps the place it has always had; only the tab that links to the wizard moves.
 */
export const BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS = {
  entry: 'onboarding',
  /**
   * The wizard's first step, and the one address every way into the wizard leads to: the entry above,
   * the Onboarding tab, the area's own default for a project that has not been onboarded, the App's
   * route for the entry, and the second step's Back. So it opens with the onboardings the project can
   * resume, when it has any, and the domain form follows once the person chooses to onboard a new
   * site; the form has no address of its own, and no link names this one as the form's.
   */
  domain: 'onboarding/domain',
  keyContent: 'onboarding/key-content',
  stepKeyContent: 'onboarding/:onboardingId/key-content',
  stepTaxonomies: 'onboarding/:onboardingId/taxonomies',
  stepTaxonomyTermsPrimary: 'onboarding/:onboardingId/taxonomy-terms-primary',
  stepTaxonomyTermsStrategic: 'onboarding/:onboardingId/taxonomy-terms-strategic',
  stepPrompts: 'onboarding/:onboardingId/prompts',
  stepReview: 'onboarding/:onboardingId/review',
} as const;

/** How many steps the wizard walks, so every step reports the same progress. */
export const BRAND_PRESENCE_ONBOARDING_TOTAL_STEPS = 7;

/** One onboarding step's address for the wizard it belongs to. */
export function brandPresenceOnboardingStepPath(routePath: string, onboardingId: string): string {
  return brandPresenceAbsolutePath(routePath.replace(':onboardingId', encodeURIComponent(onboardingId)));
}

/** The remaining route paths of the nested block, relative to {@link BRAND_PRESENCE_BASE_PATH}. */
export const BRAND_PRESENCE_AREA_ROUTE_PATHS = {
  contentInventory: 'content-inventory',
  contentInventoryItem: 'content-inventory/:key',
  contentInventoryItemStatus: 'content-inventory/:key/:statusOrId',
  issues: 'issues',
  analytics: 'analytics',
  analyticsContentHealth: 'analytics/content-health',
  analyticsContentHealthType: 'analytics/content-health/:type',
  analyticsAiVisibility: 'analytics/ai-visibility',
  analyticsAiVisibilityType: 'analytics/ai-visibility/:type',
  analyticsAiAnswers: 'analytics/ai-answers',
  analyticsAiAnswersRun: 'analytics/ai-answers/:runId',
  optimizeType: 'optimize/:type',
  optimizeTypeTaskGroup: 'optimize/:type/:taskGroupId',
  jobs: 'jobs',
  jobGroups: 'jobs/groups',
  jobTasks: 'jobs/tasks',
  jobGroup: 'jobs/groups/:id',
  jobTask: 'jobs/tasks/:id',
  settings: 'settings',
  settingsTaxonomies: 'settings/taxonomies/*',
  settingsMonitoring: 'settings/monitoring',
  settingsMonitoringPromptRuns: 'settings/monitoring/:promptId/runs',
  settingsMonitoringPromptRun: 'settings/monitoring/:promptId/runs/:runId',
  settingsMonitoringPromptRunNew: 'settings/monitoring/:promptId/run',
  settingsCrawling: 'settings/crawling',
} as const;

/**
 * The `brand-presence/…` route paths declared outside the nested block, absolute from the router
 * root. The ones the nested block also declares are shadowed by it, since it is declared first and
 * the patterns are identical; they are kept here so the whole area's URL surface is in one place.
 */
export const BRAND_PRESENCE_TOP_LEVEL_ROUTE_PATHS = {
  settings: 'brand-presence/settings',
  settingsMonitoringPromptRuns: 'brand-presence/settings/monitoring/:promptId/runs',
  settingsMonitoringPromptRun: 'brand-presence/settings/monitoring/:promptId/runs/:runId',
  settingsMonitoringPromptRunNew: 'brand-presence/settings/monitoring/:promptId/run',
  settingsCrawling: 'brand-presence/settings/crawling',
  monitoringPromptRuns: 'brand-presence/monitoring/:promptId/runs',
  monitoringPromptRun: 'brand-presence/monitoring/:promptId/runs/:runId',
  monitoringPromptRunNew: 'brand-presence/monitoring/:promptId/run',
  legacyCrawlingSettings: 'brand-presence/crawling-settings',
  legacyMonitoringPromptRuns: 'brand-presence/monitoring-prompts/:promptId/runs',
  legacyMonitoringPromptRun: 'brand-presence/monitoring-prompts/:promptId/runs/:runId',
  legacyMonitoringPromptRunNew: 'brand-presence/monitoring-prompts/:promptId/run',
} as const;

/** Turns a route path of the nested block into the absolute path a link or a redirect uses. */
export function brandPresenceAbsolutePath(routePath: string): string {
  return routePath ? `${BRAND_PRESENCE_BASE_PATH}/${routePath}` : BRAND_PRESENCE_BASE_PATH;
}

export const BRAND_PRESENCE_ONBOARDING_START_PATH = brandPresenceAbsolutePath(BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS.domain);
export const BRAND_PRESENCE_CONTENT_INVENTORY_PATH = brandPresenceAbsolutePath(BRAND_PRESENCE_AREA_ROUTE_PATHS.contentInventory);
export const BRAND_PRESENCE_ISSUES_PATH = brandPresenceAbsolutePath(BRAND_PRESENCE_AREA_ROUTE_PATHS.issues);
export const BRAND_PRESENCE_AI_VISIBILITY_PATH = brandPresenceAbsolutePath(BRAND_PRESENCE_AREA_ROUTE_PATHS.analyticsAiVisibility);
export const BRAND_PRESENCE_CONTENT_HEALTH_PATH = `${brandPresenceAbsolutePath(BRAND_PRESENCE_AREA_ROUTE_PATHS.analyticsContentHealth)}/page`;
export const BRAND_PRESENCE_AI_ANSWERS_PATH = brandPresenceAbsolutePath(BRAND_PRESENCE_AREA_ROUTE_PATHS.analyticsAiAnswers);
export const BRAND_PRESENCE_JOBS_PATH = brandPresenceAbsolutePath(BRAND_PRESENCE_AREA_ROUTE_PATHS.jobGroups);
// The route matches a subtree, so the link target is the route path without its splat suffix.
export const BRAND_PRESENCE_TAXONOMIES_PATH = brandPresenceAbsolutePath(
  BRAND_PRESENCE_AREA_ROUTE_PATHS.settingsTaxonomies.replace(/\/\*$/, '')
);
export const BRAND_PRESENCE_MONITORING_PATH = brandPresenceAbsolutePath(BRAND_PRESENCE_AREA_ROUTE_PATHS.settingsMonitoring);
export const BRAND_PRESENCE_CRAWLING_SETTINGS_PATH = brandPresenceAbsolutePath(BRAND_PRESENCE_AREA_ROUTE_PATHS.settingsCrawling);

/** Where the area opens once the onboarding wizard has been finished for the project. */
export const BRAND_PRESENCE_DEFAULT_PATH = BRAND_PRESENCE_AI_VISIBILITY_PATH;

/**
 * The query parameters the issue list reads its filters from.
 *
 * They are URL vocabulary and never a word a reader sees. They live here with the paths because a link
 * to the list is a navigation target like any other: the screen that sends a reader there and the
 * screen that reads the link apart cannot spell a parameter differently while both take it from here.
 */
export const BRAND_PRESENCE_ISSUES_QUERY_PARAMS = {
  status: 'status',
  issueType: 'issueType',
  responsibility: 'responsibility',
  page: 'page',
  site: 'site',
  pageSize: 'pageSize',
  view: 'view',
} as const;

/** The view token that groups the issue list by page; an address without it shows the flat list. */
export const BRAND_PRESENCE_ISSUES_VIEW_BY_PAGE_TOKEN = 'by-page';

/**
 * The short token each issue status round-trips through, so the URL carries no wire value.
 *
 * The map is exhaustive, so a status added to the enum fails the build here.
 */
export const BRAND_PRESENCE_ISSUE_STATUS_QUERY_TOKENS: Record<IssueStatus, string> = {
  [IssueStatus.Open]: 'open',
  [IssueStatus.Fixed]: 'fixed',
  [IssueStatus.Ignored]: 'ignored',
  [IssueStatus.FalsePositive]: 'false-positive',
};

/** The status token that clears the status filter and shows every status. */
export const BRAND_PRESENCE_ISSUE_STATUS_ANY_TOKEN = 'any';

/**
 * The issue list narrowed to one page, and to one status where the caller names one.
 *
 * A screen that shows how many issues a page has links here with the very status it counted, so the
 * number and the rows behind it are one set rather than two a reader has to reconcile. The page is
 * named by the key the issue list matches a page on, which is the key the page's own screens carry.
 */
export function brandPresencePageIssuesPath({ contentItemKey, status }: { contentItemKey: string; status?: IssueStatus }): string {
  const query = new URLSearchParams({ [BRAND_PRESENCE_ISSUES_QUERY_PARAMS.page]: contentItemKey });
  if (status) {
    query.set(BRAND_PRESENCE_ISSUES_QUERY_PARAMS.status, BRAND_PRESENCE_ISSUE_STATUS_QUERY_TOKENS[status]);
  }
  return `${BRAND_PRESENCE_ISSUES_PATH}?${query.toString()}`;
}

/** The issue list narrowed to one issue type, named by the type's stable key. */
export function brandPresenceIssueTypeIssuesPath({ issueTypeKey }: { issueTypeKey: string }): string {
  const query = new URLSearchParams({ [BRAND_PRESENCE_ISSUES_QUERY_PARAMS.issueType]: issueTypeKey });
  return `${BRAND_PRESENCE_ISSUES_PATH}?${query.toString()}`;
}

/**
 * The query parameters the content list reads its filters from. `terms` is a comma-separated list
 * of tag keys, all of which a page must carry; `status` a comma-separated list of content item
 * statuses; `type` the one content type the list is narrowed to; `pageSize` the rows a page of the
 * list shows, when the reader chose other than the default.
 */
export const BRAND_PRESENCE_CONTENT_INVENTORY_QUERY_PARAMS = {
  status: 'status',
  terms: 'terms',
  type: 'type',
  pageSize: 'pageSize',
} as const;

/**
 * The statuses the analytics figures count: every status but a page's previous revisions, which
 * Sync Core's statistics leave out. The content list asked for no status shows the published items
 * alone, so a link from a figure names these.
 */
export const BRAND_PRESENCE_COUNTED_CONTENT_ITEM_STATUSES: ContentItemStatus[] = (
  Object.values(ContentItemStatus) as ContentItemStatus[]
).filter((status) => status !== ContentItemStatus.PreviousRevision);

/**
 * The content list narrowed to the items of one type that carry every one of the given tags, at
 * every status the analytics figures count — so a figure and the list it opens are one set.
 */
export function brandPresenceTaggedContentPath({ type, termKeys }: { type: ContentRepresentation; termKeys: string[] }): string {
  const query = new URLSearchParams({
    [BRAND_PRESENCE_CONTENT_INVENTORY_QUERY_PARAMS.type]: type,
    [BRAND_PRESENCE_CONTENT_INVENTORY_QUERY_PARAMS.terms]: Array.from(new Set(termKeys)).join(','),
    [BRAND_PRESENCE_CONTENT_INVENTORY_QUERY_PARAMS.status]: BRAND_PRESENCE_COUNTED_CONTENT_ITEM_STATUSES.join(','),
  });
  return `${BRAND_PRESENCE_CONTENT_INVENTORY_PATH}?${query.toString()}`;
}

/**
 * The segment each content item status round-trips through, so the URL carries no wire value.
 *
 * The map is exhaustive, so a status added to the enum fails the build here.
 */
export const BRAND_PRESENCE_CONTENT_ITEM_STATUS_SEGMENTS: Record<ContentItemStatus, string> = {
  [ContentItemStatus.Queued]: 'queued',
  [ContentItemStatus.Fetched]: 'fetched',
  [ContentItemStatus.Classified]: 'classified',
  [ContentItemStatus.ProcessedStatically]: 'processed-statically',
  [ContentItemStatus.Draft]: 'draft',
  [ContentItemStatus.Suggestion]: 'suggestion',
  [ContentItemStatus.Published]: 'published',
  [ContentItemStatus.Unpublished]: 'unpublished',
  [ContentItemStatus.PreviousRevision]: 'previous-revision',
};

/**
 * One page's own screens, named by the key every screen of the area carries it under.
 *
 * The status is part of the address because a page is read at one of its revisions; a caller that
 * names none, or names one this map does not declare, addresses the published revision, which is
 * what the content list shows by default.
 */
export function brandPresenceContentItemPath({ key, status }: { key: string; status?: ContentItemStatus }): string {
  const segment =
    (status === undefined ? undefined : BRAND_PRESENCE_CONTENT_ITEM_STATUS_SEGMENTS[status]) ??
    BRAND_PRESENCE_CONTENT_ITEM_STATUS_SEGMENTS[ContentItemStatus.Published];
  return `${BRAND_PRESENCE_CONTENT_INVENTORY_PATH}/${encodeURIComponent(key)}/${encodeURIComponent(segment)}`;
}

/**
 * The query parameters a page's detail screen reads: the tab a link opens it on, and the health
 * check that tab highlights and pre-selects. Both travel in the address, so a host page that mirrors
 * the frame's path and query restores the same view.
 */
export const BRAND_PRESENCE_CONTENT_ITEM_QUERY_PARAMS = {
  tab: 'tab',
  check: 'check',
} as const;

/** The tab token that opens a page's detail on its Health tab. */
export const BRAND_PRESENCE_CONTENT_ITEM_HEALTH_TAB_TOKEN = 'health';

/**
 * A page's detail opened on its Health tab with one check highlighted and pre-selected, so the fix
 * for exactly that check is in view and one click from Optimize.
 */
export function brandPresenceContentItemHealthPath({ key, healthCheckKey }: { key: string; healthCheckKey: string }): string {
  const query = new URLSearchParams({
    [BRAND_PRESENCE_CONTENT_ITEM_QUERY_PARAMS.tab]: BRAND_PRESENCE_CONTENT_ITEM_HEALTH_TAB_TOKEN,
    [BRAND_PRESENCE_CONTENT_ITEM_QUERY_PARAMS.check]: healthCheckKey,
  });
  return `${brandPresenceContentItemPath({ key })}?${query.toString()}`;
}

/** The status a segment of that address names, or nothing where it names none. */
export function brandPresenceContentItemStatusFromSegment(segment: string | undefined): ContentItemStatus | undefined {
  const normalized = segment?.trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }

  return (Object.values(ContentItemStatus) as ContentItemStatus[]).find(
    (status) => normalized === status.toLowerCase() || normalized === BRAND_PRESENCE_CONTENT_ITEM_STATUS_SEGMENTS[status]
  );
}

/** The path a handover addresses, without the query string or fragment the host page carries with it. */
export function stripQueryString(path: string): string {
  const queryIndex = path.search(/[?#]/);
  return queryIndex === -1 ? path : path.substring(0, queryIndex);
}

/**
 * Whether a handover path from the host page names a screen inside the area.
 *
 * Only such a path suppresses the area default, because only such a path is something the route-sync
 * hook will open. The area's own bare path, a path outside the area, and no path at all all mean the
 * host page named no screen, and the area is free to choose where it opens.
 *
 * The host page carries its query string through its own redirects and may or may not leave a
 * trailing slash behind, so `/brand-presence/?ref=x` is the bare path and has to read as one — a
 * comparison that took it for a screen would leave the area with nothing to render at all.
 */
export function namesBrandPresenceScreen(path: string | undefined): boolean {
  if (typeof path !== 'string') {
    return false;
  }

  const addressed = stripQueryString(path);
  const withoutTrailingSlash = addressed.endsWith('/') ? addressed.slice(0, -1) : addressed;

  return withoutTrailingSlash.startsWith(`${BRAND_PRESENCE_BASE_PATH}/`);
}

/** The area's bare path, with or without the trailing slash a router may hand back. */
export function isBrandPresenceBasePath(pathname: string): boolean {
  return pathname === BRAND_PRESENCE_BASE_PATH || pathname === `${BRAND_PRESENCE_BASE_PATH}/`;
}
